import { useEffect, useMemo, useState } from "react";
import { Loader2, Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { toLatinDigits } from "@/lib/wmDigits";
import { DK_TIMEOUT_MS, dkError, dkMessage, fmt, fmtQuantity, sanitizeQuantity } from "./dkUtils";
import { OfflineBox, cardClass } from "./widgets";

const S = OPS.dk.setup;
// [key, unit label key, kind]: "int" is stored as is; "kg" / "pc" are milli
// quantities shown in kilos / pieces. The server re-checks every value.
const FIELDS = [
    ["flag_pct", "dk_setup_unit_pct", "int"],
    ["flag_min_kg_milli", "dk_unit_kg", "kg"],
    ["flag_min_pc_milli", "dk_unit_pc", "pc"],
    ["max_kg", "dk_unit_kg", "int"],
    ["max_pc", "dk_unit_pc", "int"],
    ["counter_edit_days", "dk_setup_unit_days", "int"],
];

function toForm(settings) {
    const f = {};
    FIELDS.forEach(([k, , kind]) => {
        const v = settings?.[k];
        f[k] = v === null || v === undefined ? "" : String(kind === "int" ? v : v / 1000);
    });
    f.location_codes = Array.isArray(settings?.location_codes) ? settings.location_codes : [];
    return f;
}

function fromField(kind, raw) {
    const s = String(raw ?? "").trim();
    if (s === "" || s === ".") return null;
    const n = Number(s);
    if (Number.isNaN(n)) return null;
    return kind === "int" ? parseInt(s, 10) : Math.round(n * 1000);
}

/** The branch locations an admin can switch the daily count on for: the stock
 *  count's branch list when readable (it lists every branch), else the scope's own. */
async function loadBranchLocations(scope) {
    try {
        const r = await api.get("/sc/locations");
        const list = (Array.isArray(r.data) ? r.data : r.data?.locations || []).filter((l) => (l.kind || "branch") === "branch");
        if (list.length) return list;
    } catch {
        /* no stock-count access: the daily-count locations will do */
    }
    return scope?.locations || [];
}

/**
 * «الإعدادات» (reviewers): the flag rule (% of theoretical and the minimum
 * quantity per unit class), the hard caps per field, the counter's edit window
 * in days, and the branch locations that keep the daily count. PATCH sends only
 * the changed keys; a server field error shows under its input.
 */
export function SetupAdmin({ scope, onChanged }) {
    const { t, tn, lang } = useI18n();
    const [settings, setSettings] = useState(null);
    const [form, setForm] = useState(() => toForm(scope?.settings));
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [failed, setFailed] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [branches, setBranches] = useState(scope?.locations || []);

    useEffect(() => {
        let alive = true;
        setFailed(false);
        api.get("/dk/settings")
            .then((r) => {
                if (!alive) return;
                const s = r.data?.settings && typeof r.data.settings === "object" ? r.data.settings : r.data;
                setSettings(s);
                setForm(toForm(s));
            })
            .catch((e) => {
                if (!alive) return;
                const msg = dkMessage(e, lang);
                if (msg) toast.error(msg);
                setFailed(true);
            });
        return () => { alive = false; };
    }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let alive = true;
        loadBranchLocations(scope).then((list) => { if (alive) setBranches(list); });
        return () => { alive = false; };
    }, [scope]);

    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => { if (!e[k]) return e; const n = { ...e }; delete n[k]; return n; });
    };
    const toggleCode = (code, on) => set("location_codes",
        on ? [...new Set([...form.location_codes, code])] : form.location_codes.filter((c) => c !== code));

    const changed = useMemo(() => {
        const out = {};
        if (!settings) return out;
        FIELDS.forEach(([k, , kind]) => {
            const v = fromField(kind, form[k]);
            if (v !== null && v !== settings[k]) out[k] = v;
        });
        const a = [...form.location_codes].sort();
        const b = [...(settings.location_codes || [])].sort();
        if (JSON.stringify(a) !== JSON.stringify(b)) out.location_codes = a;
        return out;
    }, [form, settings]);
    const dirty = Object.keys(changed).length > 0;

    const codeRows = useMemo(() => {
        const list = branches.filter((l) => l.code);
        const known = new Set(list.map((l) => l.code));
        return [...list, ...form.location_codes.filter((c) => !known.has(c)).map((c) => ({ id: c, code: c }))];
    }, [branches, form.location_codes]);

    const save = async () => {
        const local = {};
        FIELDS.forEach(([k, , kind]) => {
            if (form[k] === "" || fromField(kind, form[k]) === null || fromField(kind, form[k]) < 0) local[k] = t("dk_setup_err_value");
        });
        if (!form.location_codes.length) local.location_codes = t("dk_setup_err_codes");
        if (Object.keys(local).length) { setErrors(local); return; }
        setSaving(true);
        setErrors({});
        try {
            const r = await api.patch("/dk/settings", changed, { timeout: DK_TIMEOUT_MS });
            const s = r.data?.settings && typeof r.data.settings === "object" ? r.data.settings : r.data;
            if (s && typeof s === "object" && "flag_pct" in s) {
                setSettings(s);
                setForm(toForm(s));
            } else {
                setReloadKey((k) => k + 1);
            }
            toast.success(t("dk_setup_saved"));
            onChanged && onChanged();
        } catch (e) {
            const d = dkError(e);
            if (d?.field) setErrors({ [d.field]: lang === "ar" ? d.message_ar : d.message_en });
            toast.error(dkMessage(e, lang) || t("dk_offline_error"));
        } finally {
            setSaving(false);
        }
    };

    if (failed) return <OfflineBox onRetry={() => setReloadKey((k) => k + 1)} />;
    if (!settings) return <InlineLoader />;

    return (
        <div data-testid={S.view} className="space-y-3">
            <section className={`${cardClass} space-y-3 p-3`}>
                <div>
                    <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("dk_setup_rules_title")}</h3>
                    <p className="text-[11px] leading-relaxed text-[color:var(--ink-muted)]">{t("dk_setup_rules_hint")}</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                    {FIELDS.map(([k, unitKey, kind]) => (
                        <label key={k} className="block min-w-0 space-y-1">
                            <span className="block text-xs font-bold text-[color:var(--ink)]">{t(`dk_setup_${k}`)}</span>
                            <div className="relative" dir="ltr">
                                <Input data-testid={S.field(k)} value={form[k]} dir="ltr" autoComplete="off"
                                       inputMode={kind === "kg" ? "decimal" : "numeric"}
                                       onChange={(e) => set(k, kind === "kg"
                                           ? sanitizeQuantity(e.target.value, true)
                                           : toLatinDigits(e.target.value).replace(/[^\d]/g, ""))}
                                       className={`h-11 pe-16 text-base tabular-nums ${errors[k] ? "border-[color:var(--danger)]" : ""}`} />
                                <span className="pointer-events-none absolute inset-y-0 end-3 flex max-w-[3.5rem] items-center truncate text-xs font-medium text-[color:var(--ink-muted)]">
                                    {t(unitKey)}
                                </span>
                            </div>
                            <span className="block text-[11px] leading-snug text-[color:var(--ink-faint)]">{t(`dk_setup_${k}_hint`)}</span>
                            {errors[k] ? <span className="block text-[11px] font-semibold text-[color:var(--danger)]">{errors[k]}</span> : null}
                        </label>
                    ))}
                </div>
            </section>

            <section className={`${cardClass} space-y-2 p-3`}>
                <div>
                    <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("dk_setup_locations_title")}</h3>
                    <p className="text-[11px] leading-relaxed text-[color:var(--ink-muted)]">
                        {fmt(t("dk_setup_locations_hint"), { n: fmtQuantity(form.location_codes.length, lang) })}
                    </p>
                </div>
                <div data-testid={S.field("location_codes")} className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                    {codeRows.map((l) => {
                        const on = form.location_codes.includes(l.code);
                        return (
                            <label key={l.code} data-testid={S.locationCode(l.code)} data-checked={on ? "true" : "false"}
                                   className="flex min-w-0 cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] border border-[color:var(--border-soft)] px-2.5 py-2">
                                <Checkbox checked={on} onCheckedChange={(v) => toggleCode(l.code, !!v)} />
                                <span className="min-w-0">
                                    <span className="block truncate text-xs text-[color:var(--ink)]">{l.name_ar || l.name_en ? tn(l) : l.code}</span>
                                    <bdi dir="ltr" className="block text-[10px] text-[color:var(--ink-faint)]">{l.code}</bdi>
                                </span>
                            </label>
                        );
                    })}
                </div>
                {errors.location_codes ? <p className="text-[11px] font-semibold text-[color:var(--danger)]">{errors.location_codes}</p> : null}
            </section>

            <div className="sticky bottom-0 z-10 flex justify-end bg-[color:var(--bg-app)] py-1">
                <Button data-testid={S.save} onClick={save} disabled={!dirty || saving} className="h-11 w-full gap-2 px-6 text-sm font-bold sm:w-auto">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {saving ? t("dk_saving") : t("dk_save")}
                </Button>
            </div>
        </div>
    );
}
